import {Button, Dialog} from '@bosch/react-frok';
import React, {useState} from 'react';
import cloneDeep from 'lodash/cloneDeep';
import {
    CheckboxStyled,
    HeaderWarning,
    HeaderWarningDetails,
    TextInputLong,
    TextWrapper
} from './FolderMetaDataDialog.style';
import {t} from 'i18next';
import {ObjectType} from '../../../models/ObjectType';
import {StorageObject} from '../../../models/StorageObject';
import {TagPicker} from '../../TagPicker/TagPicker';
import {Accordion} from '../../Accordion/Accordion';
import {AdvancedLocalePicker} from '../../LocalePicker/AdvancedLocalePicker';
import {RangeDatePicker} from '../../DatePicker/RangeDatePicker';
import {
    getEmptyStorageObject,
    getLabelEntryMapInitialized,
    getLabelMapKeys,
    getLabelsAsObjectMap,
    setLabelEntryMap,
    validateLabelEntryMap
} from '../../../utils/StorageObjectUtils';
import {FolderBasicData} from './FolderBasicData';
import {LabelView} from '../../LabelView/LabelView';
import {Label} from '../../../models/Label';
import {StorageObjectDTO} from '../../../models/dto/StorageObjectDTO';
import {getEmptyStorageObjectDTO} from '../../../utils/DtoUtils';
import {BreadcrumbsDTO} from '../../../models/dto/BreadcrumbsDTO';
import EventEmitter from 'events';
import {AdvancedLocalePicker_addAllLocales} from '../../../init/events';
import {getDateStart, getEffectiveDate, getPath, isNullOrEmpty} from '../../../utils';
import {Valid} from '../../validation/Valid';
import {AdvancedRolesPicker} from '../../RolePicker/AdvancedRolesPicker';

export interface FolderMetaDataProps {
    readonly item: StorageObject | null;
    readonly name: string;
    readonly breadcrumbs: BreadcrumbsDTO[];
    readonly currentPath: string;
    readonly rolesDataSource: string[];
    readonly parentOwnerRoles: string[];
    readonly parentRoles: string[];
    readonly parentLocales: string[];
    readonly language: string;
    readonly defaultLocale: string;
    onClose: () => void;
    onSave: (storageObjectDTO: StorageObjectDTO, addMore: boolean) => void;
}

/**s
 * @name FolderMetaDataDialog
 * @description
 * The dialog to add the metadata of a newly created folder or edited folder
 */
export const FolderMetaDataDialog: React.VFC<FolderMetaDataProps> = (props) => {
    const [nameInput] = useState(props.item?.name ? props.item?.name : props.name);
    const [dateValidFromInput, setDateValidFromInput] = useState<Date>(props.item?.metadata?.dateValidFrom ? new Date(props.item?.metadata?.dateValidFrom) : getEffectiveDate());
    const [dateValidToInput, setDateValidToInput] = useState<Date | undefined>(props.item?.metadata?.dateValidTo ? new Date(props.item?.metadata?.dateValidTo) : undefined);
    const [labelsInput, setLabelsInput] = useState<Map<string, Label>>(getLabelEntryMapInitialized(props.defaultLocale, nameInput, props.item?.labels));
    const [versionInput, setVersionInput] = useState(props.item?.metadata?.version ?? '');
    const [tagsInput, setTagsInput] = useState<string[]>(props.item?.metadata?.tags ?? []);
    const [localesInput, setLocalesInput] = useState<string[]>(getLabelMapKeys(props.defaultLocale, props.item?.labels));
    const [isPublicInput] = useState<boolean>(props.item?.isPublic ? props.item?.isPublic : false);
    const [rolesInput, setRolesInput] = useState<string[]>(props.item?.metadata?.roles ?? []);
    const [commentInput, setCommentInput] = useState(props.item?.metadata?.comment ?? '');
    const [rolesDataSource] = useState<string[]>(props.rolesDataSource ?? []);
    const [parentOwnerRolesInput] = useState(props.parentOwnerRoles ?? []);
    const [parentRolesInput] = useState(props.parentRoles ?? []);
    const [parentLocalesInput] = useState(props.parentLocales ?? []);
    //initial state of objects that have to be reverted on reset event
    const [labelsInitialState] = useState<Map<string, Label>>(cloneDeep(labelsInput));
    const [versionInitialState] = useState(cloneDeep(versionInput));
    const [localesInitialState] = useState(cloneDeep(localesInput));
    const eventEmitter = new EventEmitter();

    const handleLocales = (locales: string[]) => {
        setLocalesInput(locales);
        let labels = setLabelEntryMap(locales, labelsInput);
        setLabelsInput(labels);
    };

    const handleAddAllLocales = () => {
        eventEmitter.emit(AdvancedLocalePicker_addAllLocales);
    };

    const handleResetLocales = () => {
        setLabelsInput(cloneDeep(labelsInitialState));
        setLocalesInput(cloneDeep(localesInitialState));
    };

    const handleChangeLabels = (labels: Map<string, Label>) => {
        setLabelsInput(labels);
        setLocalesInput(getLabelMapKeys(props.defaultLocale, labels));
    };

    const handleResetLabelsComponent = () => {
        handleResetLocales();
        setVersionInput(cloneDeep(versionInitialState));
    };

    const handleChangeVersion = (version: string) => {
        setVersionInput(version);
    };

    const handleDateRange = (dateValidFrom?: Date, dateValidTo?: Date) => {
        if ((dateValidFrom !== undefined) && dateValidFrom) {
            dateValidFrom = getDateStart(dateValidFrom);
            if (dateValidFromInput !== dateValidFrom) {
                setDateValidFromInput(dateValidFrom);
            }
        }

        if ((dateValidTo !== undefined) && dateValidTo) {
            dateValidTo = getDateStart(dateValidTo);
            if (dateValidToInput !== dateValidTo) {
                setDateValidToInput(dateValidTo);
            }
        } else {
            setDateValidToInput(undefined);
        }
    };

    const handleTags = (tags: string[]) => {
        setTagsInput(tags);
    };

    const handleRoles = (userRoles: string[]) => {
        setRolesInput(userRoles);
    };

    const handleCommentInput = (e: { target: { value: React.SetStateAction<string> } }) => {
        let eventValue = e.target.value.toString();
        setCommentInput(eventValue);
    };

    // const isValidComment = (): boolean => {
    //     if ((!!props.item) && (isValidText(props.item.id))) {
    //         return isValidText(commentInput);
    //     }
    //
    //     return true;
    // };

    let isValidCommentInput = true; // isValidComment(); //Task DXF-3159 temporary solution until template rules implementation

    const canExecuteSave = (): boolean => {
        return ((!isNullOrEmpty(versionInput)) && validateLabelEntryMap(labelsInput) && isValidCommentInput) as boolean;
    };

    let disableSaveOperation = (!canExecuteSave());

    const handleSave = (addMore: boolean) => {
        let storageObjectDTO = getEmptyStorageObjectDTO();
        let storageObject;
        if ((props.item !== undefined) && props.item) {
            storageObject = props.item;
        } else {
            storageObject = getEmptyStorageObject(ObjectType.FOLDER);
            if ((storageObject) && ((storageObject.id === undefined) || (isNullOrEmpty(storageObject.id)))) {
                storageObject.name = nameInput;
                storageObject.path = decodeURI(props.currentPath);
                storageObject.fullPath = getPath(storageObject.path, nameInput);
            }
        }

        storageObject.isPublic = isPublicInput;
        storageObject.labels = getLabelsAsObjectMap(labelsInput);
        storageObject.metadata.version = versionInput.trim();
        storageObject.metadata.comment = commentInput.trim();
        storageObject.metadata.dateValidFrom = dateValidFromInput?.getTime();
        storageObject.metadata.dateValidTo = dateValidToInput?.getTime();
        storageObject.metadata.parentOwnerRoles = parentOwnerRolesInput;
        storageObject.metadata.parentRoles = parentRolesInput;
        storageObject.metadata.roles = rolesInput;
        storageObject.metadata.tags = tagsInput;
        storageObjectDTO.item = storageObject;
        props.onSave(storageObjectDTO, addMore);
    };

    const actionButtons = () => {
        return (
            <>
                <Button key='cancel' mode='secondary' type='button' label={t('GENERAL_CANCEL_LABEL')}
                        onClick={props.onClose}/>
                <Button
                    key='saveExit'
                    icon='upload'
                    mode='secondary'
                    type='button'
                    label={t('GENERAL_SAVE_EXIT_LABEL')}
                    onClick={() => handleSave(false)}
                    disabled={disableSaveOperation}
                />
                <Button
                    key='saveAddNew'
                    icon='upload'
                    mode='primary'
                    type='button'
                    label={t('GENERAL_SAVE_ADD_NEW_LABEL')}
                    onClick={() => handleSave(true)}
                    disabled={disableSaveOperation}
                />
            </>
        );
    };

    return (
        <Dialog
            modal
            open
            title={!!props.item?.id ? t('CREATE_EDIT_FOLDER_DIALOG_TITLE_EDIT') : t('CREATE_EDIT_FOLDER_DIALOG_TITLE_CREATE')}
            onClose={props.onClose}
        >
            {props.item?.toRemove &&
            <>
                <HeaderWarning>{t('CREATE_EDIT_FOLDER_DIALOG_EDIT_EXISTING_ITEM')}</HeaderWarning>
                <HeaderWarningDetails>{t('CREATE_EDIT_FOLDER_DIALOG_EDIT_MARKED_FOR_DELETION_ITEM')}</HeaderWarningDetails>
            </>
            }

            <Accordion accordionHeadline={t('CREATE_EDIT_FOLDER_DIALOG_ACCORDION_TITLE_BASIC_INFORMATION')}>
                <FolderBasicData language={props.language}
                                 item={props.item}
                                 name={nameInput}
                />
            </Accordion>

            <Accordion accordionHeadline={t('LOCALE_LABELS_CAPTION')}
                       startOpen>
                <LabelView defaultLocale={props.defaultLocale}
                           labels={labelsInput}
                           version={versionInput}
                           onLabelsChange={handleChangeLabels}
                           onVersionChange={handleChangeVersion}
                           onAddAllLabels={handleAddAllLocales}
                           onReset={handleResetLabelsComponent}
                />
            </Accordion>

            <Accordion accordionHeadline={t('LOCALE_CAPTION')}>
                <AdvancedLocalePicker defaultLocale={props.defaultLocale}
                                      locales={localesInput}
                                      parentLocales={parentLocalesInput}
                                      localesToDelete={[]}
                                      setParentLocales={false}
                                      setLocalesToDelete={() => {
                                      }}
                                      eventEmitter={eventEmitter}
                                      onChange={handleLocales}
                                      onReset={handleResetLocales}
                />
            </Accordion>

            <Accordion accordionHeadline={t('VALIDITY_CAPTION')}>
                <RangeDatePicker dateFrom={dateValidFromInput}
                                 labelFrom={'DATE_FROM'}
                                 initializeFrom={true}
                                 isClearableFrom={true}
                                 isNullableFrom={false}
                                 dateTo={dateValidToInput}
                                 labelTo={'DATE_TO'}
                                 initializeTo={false}
                                 isClearableTo={true}
                                 isNullableTo={true}
                                 onChange={handleDateRange}
                />
            </Accordion>

            <Accordion accordionHeadline={t('CREATE_EDIT_FOLDER_DIALOG_ACCORDION_TITLE_TAGS')}>
                <TagPicker dataSource={tagsInput}
                           onChange={handleTags}/>
            </Accordion>

            <Accordion accordionHeadline={t('CREATE_EDIT_FOLDER_DIALOG_ACCORDION_TITLE_ROLES')}>
                <CheckboxStyled key={'IsPublic'}
                                id={'IsPublic'}
                                label={t('PUBLIC_ACCESS')}
                                checked={isPublicInput}
                                disabled={true}/>
                <AdvancedRolesPicker dataSource={rolesDataSource}
                                     userRoles={rolesInput}
                                     parentRoles={parentRolesInput}
                                     onChange={handleRoles}/>
            </Accordion>

            <Accordion
                accordionHeadline={t('CREATE_EDIT_FOLDER_DIALOG_ACCORDION_TITLE_COMMENT')}
                accordionSubHeadline={' ' + t('CREATE_EDIT_FOLDER_DIALOG_ACCORDION_TITLE_COMMENT_INFO')}
                startOpen={(!isValidCommentInput)}
            >
                <Valid
                    isValid={isValidCommentInput}
                    message={t('META_DATA_DIALOG_COMMENT_NOT_VALID_CONSTRAINT')}>
                    <TextInputLong
                        id='comment'
                        label={t('CREATE_EDIT_FOLDER_DIALOG_FOLDER_COMMENT_LABEL')}
                        required
                        value={commentInput}
                        onChange={handleCommentInput}
                    />
                </Valid>
            </Accordion>

            <TextWrapper>{`* ${t('GENERAL_MANDATORY_FIELDS_INFO_TEXT')}`}</TextWrapper>
            <div className='m-dialog__actions'>{actionButtons()}</div>
        </Dialog>
    );
};
